<template>
  <TemplateBase :page="page" class="bg-gradient-to-r from-app-secondary-2-yellow--dark-portica to-app-secondary-2-orange--mango-tango">
    <!-- Slot with back button with flush false so it is positioned to the header -->
    <template #backButton>
      <MoleculeButtonBreadcrumbBack :breadcrumbs="breadcrumbs" />
    </template>

    <!-- Slot with mobile lyre link component, color is optional in white, default is black, only visible until xl screens, positioning is inside the component -->
    <template #mobileLyre>
      <MoleculeButtonMobileLyre v-if="!mobileFilterStatus" :flush="false" :white-lyre="true" />
    </template>

    <div class="grid grid-cols-12 relative">
      <!--   Section for the page header, intro and such   -->
      <section v-if="page.image" class="md:hidden col-span-full row-start-1 bg-transparent z-0">
        <MoleculeMaskVisual :image="page.image" :shape-number="7" />
      </section>
      <section v-if="page.image" class="hidden md:block absolute w-full bg-transparent z-0">
        <MoleculeMaskVisual :image="page.image" :shape-number="6" />
      </section>
      <div class="absolute z-1 top-0 bottom-0 left-0 right-0 opacity-50 v-gradient-bg v-gradient-bg--gold v-gradient-bg--lyre-only"></div>
      <section class="relative z-1 col-span-full container z-grid v-page-section-container-padding mt-6 sm:mb-4 lg:mb-10 2xl:mb-16 md:pt-16 lg:pt-32">
        <div class="relative z-20 col-span-full lg:col-start-2 md:col-end-9 xl:col-end-8 md:mr-4">
          <span class="block pointer-events-none mb-3">
            <span class="leading-6 text-6xl font-normal text-white v-clip">Ontdek</span>
            <AtomBrandDiscoveryTitleDutch class="text-app-primary-1-red--fire-brick w-48 md:w-56 lg:w-64 xl:w-76" />
          </span>
          <h1 class="text-white text-2xl md:text-3xl lg:text-5xl leading-tight font-medium mb-4">{{ page.title }}</h1>
          <div v-if="page.introduction" class="text-base font-normal md:mb-10" v-html="stripTags(page.introduction)" />
        </div>
      </section>

      <!--   Section for the collection-cards   -->
      <section v-if="discoveries.length" class="relative z-1 col-span-full container z-grid v-page-section-container-padding mt-6 mb-8 md:mb-12">
        <ul class="col-span-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-8">
          <li v-for="(discovery, index) of discoveries" :key="index" class="flex">
            <MoleculeCardOverview
              :title="discovery.title"
              :subtitle="discovery.discoveryCollections ? discovery.discoveryCollections.map(({ title }: CmsData) => title).join(', ') : undefined"
              :image="discovery.teaserImage"
              :url="discovery.url"
              :icon="get(discovery, 'discoveryCategory.icon.slug')"
              background-color="v-gradient-bg  v-gradient-bg--gold v-gradient-bg--gradient-only"
              type="discover"
            />
          </li>

          <!--discovery collection positions has a maximum position of 5, based on rows in desktop view-->
          <li
            v-for="({ position, banner }, index) of page.bannerDynamicContent"
            :key="`${index}-banner`"
            class="flex sm:col-span-2 xl:col-span-3 z-grid"
            :class="{
              'row-start-1': position === 0,
              'row-start-2': position === 1,
              'row-start-3': position === 2,
              'row-start-4': position === 3,
              'row-start-5': position === 4,
              'row-start-6': position === 5,
              'row-start-7': position === 6,
            }"
          >
            <MoleculeCardBanner
              class="col-span-full lg:col-start-2 lg:col-end-12"
              :title="banner.title"
              :top-label="banner.label"
              :sub-label="banner.subtitle"
              :icon="get(banner, 'icon.slug')"
              :image="banner.image"
              :button-title="banner.buttonTitle"
              :button-url="banner.buttonUrl"
              :theme="banner.theme"
            />
          </li>
        </ul>
      </section>

      <!-- Paging -->
      <OrganismPagination
        :count="totalHits"
        :limit="hitsPerPage"
        :page="currentPage"
        class="col-span-full mt-1 mb-4 xl:mb-8"
        @click="({ page: newPage }) => setCurrentPage(newPage)"
      />

      <!--   Section for the related collections   -->
      <section class="col-span-full container z-grid v-page-section-container-padding mt-6 mb-12">
        <div class="col-span-full lg:col-start-2 lg:col-end-12 border-t border-app-primary-grey--black-squeeze py-6 md:py-8 lg:py-12">
          <AtomHeadingPageSection class="mb-4 md:mb-8" text-color="text-app-primary-1-red--fire-brick" :title="$t('discover-our-collections')" />
          <ul class="flex flex-row flex-wrap">
            <li v-for="collection in page.otherCollections" :key="collection.url" class="mr-2 mb-4">
              <MoleculeButton :url="collection.url" size="medium" class="group-hover:bg-app-primary-1-red--fire-brick group-focus:bg-app-primary-1-red--fire-brick z-10">
                {{ collection.title }}
              </MoleculeButton>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </TemplateBase>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import { stripTags } from '@zicht/nuxt-util'

const props = defineProps({
  breadcrumbs: { type: Array as PropType<any[]>, required: true },
  limit: { type: Number, required: false, default: 15 },
  page: { type: Object as PropType<Record<string, any>>, required: true },
})

const route = useRoute()
const router = useRouter()
const mobileMenusStore = useMobileMenusStore()
const discoveryOverviewStore = useDiscoveryOverviewStore()

const mobileFilterStatus = computed(() => mobileMenusStore.mobileFilterStatus)

const currentPage = computed(() => discoveryOverviewStore.page)
const discoveries = computed(() => discoveryOverviewStore.discoveries)
const hitsPerPage = computed(() => discoveryOverviewStore.hitsPerPage)
const totalHits = computed(() => discoveryOverviewStore.totalHits)
const urlParameters = computed(() => discoveryOverviewStore.urlParameters)

function setCurrentPage(newPage: number) {
  discoveryOverviewStore.setPage(newPage)
}
function setUrlParameters(query: CmsData) {
  discoveryOverviewStore.setUrlParameters(query)
}

watch(
  () => route.query,
  (query) => {
    if (!routerQueryIsEqual(urlParameters.value, query)) {
      // hack: add the 'categories' because on this page this is always pre-set
      query.categories = [props.page.title]
      setUrlParameters(query)
    }
  },
)
watch(urlParameters, (parameters) => {
  // hack: remove the 'categories' because on this page this is always pre-set
  const query = { ...parameters, categories: undefined }
  if (!routerQueryIsEqual(route.query, query)) {
    router.push({ query })
  }
})
</script>

<style lang="scss" scoped>
.xl\:w-76 {
  @screen xl {
    width: 19rem;
  }
}
</style>
